import * as Sentry from '@sentry/browser';
import { useQueryClient } from '@tanstack/react-query';
import { publicAccountLogin } from 'api/account/auth/api';
import { getSelf } from 'api/account/me/api';
import { User } from 'api/account/me/dtos';
import { RoleTypeEnum } from 'api/account/membership/dtos';
import { Permission } from 'api/shared';
import { login, refreshToken, ssoLogin } from 'api/unauth/auth/api';
import { TokenResponse } from 'api/unauth/auth/dtos';
import { LoadingPage } from 'components/loaders';
import { jwtDecode } from 'jwt-decode';
import React, { PropsWithChildren, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { CompressedMemberships, decompressMemberships } from 'utils';
import { AuthStateEnum } from 'utils/constants/auth.constant';

const AuthContext = React.createContext<IAuthContext>({} as IAuthContext);

interface IAuthContext {
  authState: AuthStateEnum;
  signIn: (email: string, password: string) => Promise<void>;
  ssoSignIn: (idToken: string) => Promise<void>;
  publicSignin: (businessId: number) => Promise<void>;
  signOut: () => void;
  refreshUser: (refreshAuth?: boolean) => Promise<void>;
  memberships?: { permission: Permission[]; validTo: string }[];
  user?: Partial<AuthUser>;
}

interface AuthUser extends User {
  isMimic: boolean;
}

export interface JWTToken {
  claims: {
    email: string;
    originEmail: string; // used for mimic
    id: number;
    isAdmin: boolean;
    memberships: CompressedMemberships;
    role: string;
  };
  exp: number;
  iat: number;
}

const getTokenClaims = () => {
  const token = localStorage.getItem('access_token');

  if (!token) return undefined;

  try {
    const decoded = jwtDecode<JWTToken>(token);

    const parsedMemberships = Object.entries(decompressMemberships(decoded.claims.memberships)).map(
      ([businessId, value]) => ({
        ...value,
        businessId: Number(businessId),
      }),
    );

    return {
      ...decoded.claims,
      role: decoded.claims.role as RoleTypeEnum,
      memberships: parsedMemberships,
    } satisfies Partial<AuthUser>;
  } catch {
    console.error('Incorrect token');
    return undefined;
  }
};

function AuthProvider({ children }: PropsWithChildren<unknown>) {
  const [authState, setAuthState] = useState(AuthStateEnum.INITIAL);
  const [user, setUser] = useState<Partial<AuthUser> | undefined>(() => getTokenClaims());

  const queryClient = useQueryClient();

  const signOut = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setAuthState(AuthStateEnum.UNAUTHENTICATED);
    setUser(undefined);

    queryClient.clear();
  }, [queryClient]);

  const authenticate = useCallback(
    async (tokens: TokenResponse) => {
      if (!tokens.accessToken || !tokens.refreshToken) {
        signOut();
        return;
      }

      localStorage.setItem('access_token', tokens.accessToken);
      localStorage.setItem('refresh_token', tokens.refreshToken);
      localStorage.setItem('fiter:signInAt', new Date().toISOString());

      const redirect = window.sessionStorage.getItem('fiter:redirect');

      if (redirect) {
        window.sessionStorage.removeItem('fiter:redirect');
        window.location.href = redirect;
        return;
      }

      const userClaims = getTokenClaims();
      const userDetails = await getSelf();

      setUser({
        ...userDetails,
        isMimic: Boolean(userClaims?.originEmail) && userClaims?.email !== userClaims?.originEmail,
      });
      setAuthState(AuthStateEnum.AUTHENTICATED);

      // Sentry context
      const scope = Sentry.getCurrentScope();
      scope.setTag('user.role', userDetails.role);
      scope.setUser({
        id: String(userDetails.id),
        email: userDetails.email,
      });
    },
    [signOut],
  );

  const refreshUser = useCallback(async () => {
    queryClient.clear();
    const token = localStorage.getItem('refresh_token');

    if (!token) {
      signOut();
      return;
    }

    try {
      const tokenReponse = await refreshToken({ refreshToken: token });
      await authenticate(tokenReponse);
    } catch {
      signOut();
    }
  }, [authenticate, queryClient, signOut]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const tokenReponse = await login({
        username: email.toLowerCase().trim(),
        password: password,
      });

      await authenticate(tokenReponse);
    },
    [authenticate],
  );

  const publicSignin = useCallback(
    async (businessId: number) => {
      const tokenResponse = await publicAccountLogin({ businessId });

      await authenticate(tokenResponse);
    },
    [authenticate],
  );

  const ssoSignIn = useCallback(
    async (idToken: string) => {
      const tokenResponse = await ssoLogin({ idToken });

      await authenticate(tokenResponse);
    },
    [authenticate],
  );

  useLayoutEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const state = useMemo(
    () => ({
      authState,
      signIn,
      ssoSignIn,
      publicSignin,
      signOut,
      refreshUser,
      user,
    }),
    [authState, signIn, ssoSignIn, publicSignin, signOut, refreshUser, user],
  );

  return (
    <AuthContext value={state}>
      {authState === AuthStateEnum.INITIAL ? <LoadingPage /> : children}
    </AuthContext>
  );
}

export { AuthContext, AuthProvider };
